import {
  Button,
  ErrorIcon,
  InfoIcon,
  Select as SingleSelect,
  type SingleValue,
  Tooltip,
} from '@lokalise/louis'
import type {
  AiConfigLanguage,
  AiConfigProject,
  Integrations,
} from '@lokalise/translation-api-schemas'
import type { Tag } from '@lokalise/workspaces-api-schemas'
import { useState } from 'react'
import { requiredErrorMessage } from '../../../../constants/errors'
import { AI_PROFILES_DOCUMENTATION_URL } from '../../../../constants/url'
import type { CreationConfigAllowedTypes } from '../../../../types/tag'
import { CustomTargetLanguagesView } from '../../../KeysForLanguage/components/CustomTargetLanguagesView/CustomTargetLanguagesView'
import {
  KeysForLanguageBorderWrapper,
  KeysForLanguageContainer,
  KeysForLanguageContents,
  KeysForLanguageHeader,
  KeysForLanguagesSourceArrow,
  KeysForLanguagesSourceCard,
  KeysForLanguagesTargetLanguagesContainer,
} from '../../../KeysForLanguage/KeysForLanguage'
import { Select as MultiSelect } from '../../../Select/Select'
import {
  StepContainer,
  StepContents,
  StepFooter,
  StepRSP,
  StepSubtitle,
  StepTitle,
  StepTitleContainer,
} from '../../../Step/Step'
import { StyledLink } from '../../../StyledLink/StyledLink'
import { DataSource } from './components/DataSource'
import styles from './DataSourceStep.module.css'

type DataSourceStepProps = {
  sourceLangs: AiConfigLanguage[] | undefined
  targetLangs: AiConfigLanguage[]
  selectedSourceLang: AiConfigLanguage | undefined
  selectedTargetLangs: AiConfigLanguage[]
  dataSource: CreationConfigAllowedTypes | null
  projects: AiConfigProject[]
  selectedProjects: string[]
  tags: Tag[]
  name: string
  selectedTag: string | null
  integration: Integrations
  isEdit: boolean
  isLoadingLanguages?: boolean
  isLoadingTags?: boolean
  isLoadingProjects?: boolean
  isLoadingSourceLangs?: boolean
  onSourceLangChange: (lang: AiConfigLanguage | undefined) => void
  onSelectedTargetLangsChange: (langs: AiConfigLanguage[]) => void
  onDataSourceChange: (source: CreationConfigAllowedTypes) => void
  onProjectsChange: (projects: string[]) => void
  onTagChange: (tag: string | null) => void
  onClose: () => void
  onNext: () => void
}

const options: { label: string; value: CreationConfigAllowedTypes }[] = [
  { label: 'Tags', value: 'tags' },
  { label: 'Reviewed status in project', value: 'reviewed' },
]

export const DataSourceStep = ({
  targetLangs,
  selectedTargetLangs,
  sourceLangs,
  selectedSourceLang,
  dataSource,
  projects,
  selectedProjects,
  tags,
  selectedTag,
  integration,
  isEdit,
  isLoadingLanguages,
  isLoadingTags,
  isLoadingProjects,
  isLoadingSourceLangs,
  name,
  onSelectedTargetLangsChange,
  onSourceLangChange,
  onDataSourceChange,
  onProjectsChange,
  onTagChange,
  onClose,
  onNext,
}: DataSourceStepProps) => {
  const [hasClickedNext, setHasClickedNext] = useState(false)
  const [showSourceChangeWarning, setShowSourceChangeWarning] = useState(false)

  const dataSourceValue = options.find((option) => option.value === dataSource) ?? null

  const sourceLanguageError = hasClickedNext && !selectedSourceLang && requiredErrorMessage
  const targetLanguageError = hasClickedNext && !selectedTargetLangs.length && requiredErrorMessage
  const sourceError = hasClickedNext && !dataSource && requiredErrorMessage

  const onClickNext = () => {
    setHasClickedNext(true)

    if (
      selectedSourceLang &&
      selectedTargetLangs.length &&
      dataSource &&
      (selectedProjects.length || selectedTag)
    ) {
      onNext()
    }
  }

  const onSelectSourceLang = (lang: SingleValue<AiConfigLanguage | undefined>) => {
    onSourceLangChange(lang ?? undefined)
    setShowSourceChangeWarning(isEdit && lang?.id !== selectedSourceLang?.id)
  }

  return (
    <StepContainer>
      <StepContents>
        <StepTitleContainer>
          <StepTitle>Specify the data source</StepTitle>
          <StepSubtitle>
            Specify language pairs with human-reviewed translations. Provide{' '}
            <strong>500+ examples</strong> per language; more improves quality. New qualifying
            translations will be reused in the profile.{' '}
            <StyledLink href={AI_PROFILES_DOCUMENTATION_URL} rel="noreferrer" target="_blank">
              Docs.
            </StyledLink>
          </StepSubtitle>
        </StepTitleContainer>
        <div className="flex-col gap-5">
          <div className="flex-col gap-2">
            <span className={styles.heading} id="source-lang-select">
              Choose the source language
            </span>
            <SingleSelect
              aria-labelledby="source-lang-select"
              options={sourceLangs}
              value={selectedSourceLang}
              onChange={onSelectSourceLang}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id.toString()}
              isLoading={isLoadingSourceLangs}
              error={sourceLanguageError}
              placeholder="Choose language"
            />
            {showSourceChangeWarning && (
              <div className={styles.warning}>
                <ErrorIcon size="16px" />
                <span>
                  Using a source language that differs from the project's original language can
                  negatively affect the quality of translations
                </span>
              </div>
            )}
          </div>
          <MultiSelect
            title="Choose target languages"
            name="target-langs"
            placeholder="Choose language"
            options={targetLangs}
            selectedOptions={selectedTargetLangs}
            onSelectionChange={(langs) => onSelectedTargetLangsChange([...langs])}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id.toString()}
            maxVisibleOptions={5}
            isLoading={isLoadingLanguages}
            error={targetLanguageError}
          />
          <div className="flex-col gap-2">
            <div className="flex gap-1">
              <span className={styles.heading} id="source-label">
                Choose data source
              </span>
              <Tooltip
                classNames={{ content: styles.tooltip }}
                placement="bottom"
                tooltip="For greater flexibility in data selection, use tags. Tags allow you to gather data from one or multiple projects in Lokalise. If you have high-quality translations in a single project, you can also use the review status option instead."
              >
                <span className="flex items-center">
                  <InfoIcon size="16px" />
                </span>
              </Tooltip>
            </div>
            <SingleSelect
              aria-labelledby="source-label"
              placeholder="Choose source"
              options={options}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              onChange={(source) => {
                if (source === null) {
                  return
                }
                // When updating data source, reset projects + tags selection
                // Ensures we don't accidentally submit data for both tags and projects (can only choose one!)
                onProjectsChange([])
                onTagChange(null)
                onDataSourceChange(source.value)
              }}
              value={dataSourceValue}
              error={sourceError}
            />
          </div>
          <DataSource
            dataSource={dataSource}
            hasClickedNext={hasClickedNext}
            isLoadingTags={isLoadingTags}
            isLoadingProjects={isLoadingProjects}
            projects={projects}
            tags={tags}
            selectedTag={selectedTag}
            selectedProjects={selectedProjects}
            onProjectsChange={onProjectsChange}
            onTagChange={onTagChange}
          />
        </div>
        <StepFooter>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onClickNext} variant="primary">
            Next
          </Button>
        </StepFooter>
      </StepContents>
      <StepRSP>
        <KeysForLanguageContainer>
          <KeysForLanguageBorderWrapper>
            <KeysForLanguageHeader name={name} integration={integration} />
            <KeysForLanguageContents>
              <KeysForLanguagesSourceCard sourceLanguage={selectedSourceLang} highlighted />

              <KeysForLanguagesSourceArrow />

              <KeysForLanguagesTargetLanguagesContainer>
                <CustomTargetLanguagesView
                  highlighted
                  type={dataSource ?? undefined}
                  tag={selectedTag ?? undefined}
                  projects={selectedProjects}
                  sourceLangId={selectedSourceLang?.id}
                  targetLangs={selectedTargetLangs}
                />
              </KeysForLanguagesTargetLanguagesContainer>
            </KeysForLanguageContents>
          </KeysForLanguageBorderWrapper>
        </KeysForLanguageContainer>
      </StepRSP>
    </StepContainer>
  )
}
